/**
 * Static catalogue data for Ashlar Buildchem.
 * Embedded directly in frontend for standalone client-side operations.
 */

export const CATEGORIES = [
  {
    slug: "tile-adhesives",
    name: "Tile Adhesives",
    code: "ATA",
    tagline: "Polymer-modified bonding for every tile & stone.",
    image: "https://images.unsplash.com/photo-1515895309288-a3815ab7cf81?q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "grouts",
    name: "Grouts",
    code: "ATG / AEG",
    tagline: "Cementitious & epoxy joints that never stain.",
    image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "admixtures",
    name: "Admixtures & Additives",
    code: "AGA / ABL",
    tagline: "Liquid performance boosters engineered with advanced chemistry.",
    image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "block-adhesive",
    name: "Block Adhesive",
    code: "ABA",
    tagline: "Thin-bed strength for AAC blocks & bricks.",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "surface-solutions",
    name: "Plaster & Flooring",
    code: "ARM / AFH",
    tagline: "Ready-mix plaster and industrial floor hardeners.",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1600&auto=format&fit=crop",
  },
];

export const PRODUCTS = [
  {
    slug: "tile-adhesive-ceramic-ata01",
    code: "ATA01",
    name: "Tile Adhesive for Ceramic Tiles",
    category: "tile-adhesives",
    category_name: "Tile Adhesives",
    classification: "Type 1 / C1",
    tagline: "Polymer-modified cementitious adhesive for ceramic tiles in internal areas.",
    description:
      "Ashlar ATA01 is a single-component, polymer-modified cementitious tile adhesive engineered for small and medium ceramic tiles on internal walls and floors. Just add water — no sand, no cement, no curing.",
    image: "/products/ata01.jpeg",
    color: "Grey",
    packaging: ["20 KG Bag"],
    coverage: "50–60 sq.ft. per 20 kg bag at 3 mm bed thickness",
    features: [
      "Ideal for fixing ceramic tiles on internal walls & floors",
      "Polymer modified for higher bond strength",
      "Perfect for concrete & plaster substrates",
      "Single component — just add water",
      "Needs no curing",
    ],
    applications: ["Internal walls", "Internal floors", "Concrete substrates", "Plaster substrates"],
    substrates: ["Concrete", "Cement Plaster"],
  },
  {
    slug: "tile-adhesive-vitrified-ata02",
    code: "ATA02",
    name: "Tile Adhesive for Vitrified Tiles",
    category: "tile-adhesives",
    category_name: "Tile Adhesives",
    classification: "Type 2 / C2TE",
    tagline: "High-strength adhesive for vitrified & glass-mosaic tiles, internal and external.",
    description:
      "Ashlar ATA02 is a high-strength polymer-modified adhesive for ceramic and vitrified tiles across internal and external areas. A heavy-duty formulation built to withstand extreme climate on external surfaces.",
    image: "/products/ata02.jpeg",
    color: "Grey & White",
    packaging: ["20 KG Bag"],
    coverage: "50–60 sq.ft. per 20 kg bag at 3 mm bed thickness",
    features: [
      "For vitrified, glass mosaic tiles & stones",
      "Interior floor & wall + exterior floor application",
      "Heavy-duty formula for extreme climates",
      "High bond strength — engineered for reliable performance",
      "Single component — just add water",
    ],
    applications: ["Internal floor & wall", "External floor", "Brick masonry", "Cement terrazzo"],
    substrates: ["Concrete", "Cement Plaster", "Brick Masonry", "Cement Terrazzo"],
  },
  {
    slug: "tile-adhesive-stones-ata03",
    code: "ATA03",
    name: "Tile Adhesive for Stones",
    category: "tile-adhesives",
    category_name: "Tile Adhesives",
    classification: "Type 3 / C2TES1",
    tagline: "Engineered bond for natural stones & large-format tiles, inside and out.",
    description:
      "Ashlar ATA03 is a specially designed adhesive for all types of ceramic, vitrified and semi-vitrified tiles, glass mosaic, precast terrazzo and natural stones on internal and external walls — delivering exceptional bond strength.",
    image: "/products/ata03.jpeg",
    color: "Grey & White",
    packaging: ["20 KG Bag"],
    coverage: "50–60 sq.ft. per 20 kg bag at 3 mm bed thickness",
    features: [
      "For stones & large-size tiles on floors & walls",
      "Interior & exterior application",
      "High bond strength — engineered for reliable performance",
      "Tile-on-tile capable",
      "Single component — just add water",
    ],
    applications: ["Interior & exterior floors and walls", "Tile-on-tile", "Natural stone", "Precast terrazzo"],
    substrates: ["Concrete", "Cement Plaster", "Existing Tiles", "Brick Masonry"],
  },
  {
    slug: "tile-adhesive-elevation-ata04",
    code: "ATA04",
    name: "Tile Adhesive for Elevation",
    category: "tile-adhesives",
    category_name: "Tile Adhesives",
    classification: "Type 3 / C2TES1",
    tagline: "High-rise façade & swimming pool adhesive for tiles and stones at any height.",
    description:
      "Ashlar ATA04 is a high-strength polymer-based adhesive designed for exterior application of tiles and stones on high-rise elevations, façades and swimming pools. Suitable for large-format tiles and underwater areas.",
    image: "/products/ata04.jpeg",
    color: "White & Grey",
    packaging: ["20 KG Bag"],
    coverage: "50–60 sq.ft. per 20 kg bag at 3 mm bed thickness",
    features: [
      "For external tiles & stones at any height",
      "High-rise cladding, façades & swimming pools",
      "Underwater-area capable",
      "Polymer modified higher strength for demanding applications",
      "Single component — just add water",
    ],
    applications: ["High-rise façades", "Swimming pools", "Underwater areas", "Large-format cladding"],
    substrates: ["Concrete", "Cement Plaster", "Existing Tiles", "Brick Masonry"],
  },
  {
    slug: "polymer-tile-grout-atg01",
    code: "ATG01",
    name: "Polymer Modified Tile Grout",
    category: "grouts",
    category_name: "Grouts",
    classification: "Cementitious",
    tagline: "Uniform-colour, hard-wearing joints up to 3 mm.",
    description:
      "Ashlar ATG01 is a polymer-modified grout for tile & stone joints from 1–3 mm on walls and floors, internal and external. Mix with water, or with Ashlar Grout Admixture for improved strength and water resistance.",
    image: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?q=80&w=1600&auto=format&fit=crop",
    color: "Multiple colours",
    packaging: ["1 KG Pouch"],
    coverage: "Varies with joint width & tile dimensions",
    features: [
      "Polymer modified for higher strength",
      "Uniform colour, hard & durable",
      "Easy & quick application",
      "For internal & external use",
      "Best with Ashlar Grout Admixture",
    ],
    applications: ["Tile joints 1–3 mm", "Stone joints", "Wall & floor", "Internal & external"],
    substrates: ["Tile joints", "Stone joints"],
  },
  {
    slug: "epoxy-grout-aeg01",
    code: "AEG01",
    name: "Epoxy Grout for Floor & Wall",
    category: "grouts",
    category_name: "Grouts",
    classification: "3-Component Epoxy",
    tagline: "100% stain-free, chemical-resistant epoxy joints up to 12 mm.",
    description:
      "Ashlar AEG01 is a stain-free, water and chemical-resistant, polymer-modified 3-component epoxy grout for tile and stone joints up to 12 mm. A heavy-duty product for residential, commercial and industrial applications, internal and external.",
    image: "/products/aeg01.jpeg",
    color: "Multiple colours",
    packaging: ["1 KG (0.3 kg resin + 0.7 kg filler)", "5 KG (1.5 kg hardener + 3.5 kg resin)"],
    coverage: "Varies with joint width & tile dimensions",
    features: [
      "100% solid grout for joints up to 12 mm",
      "100% stain-free & germ resistant",
      "Acid, oil & water resistant",
      "Strong, durable & vibrant colours",
      "For residential, commercial & industrial use",
    ],
    applications: ["Kitchens & bathrooms", "Commercial spaces", "Industrial floors", "Swimming pools"],
    substrates: ["Tile joints up to 12 mm", "Stone joints"],
  },
  {
    slug: "epoxy-sparkles-aes01",
    code: "AES01",
    name: "Epoxy Sparkles",
    category: "grouts",
    category_name: "Grouts",
    classification: "Decorative Admix",
    tagline: "Metallic shimmer for epoxy joints — gold, silver & copper.",
    description:
      "Ashlar AES01 is a powdered admix for epoxy grout that enhances the beauty of tile joints with a sparkly, metallic special effect. Available in gold, silver and copper.",
    image: "/products/aes01.jpeg",
    color: "Gold, Silver, Copper",
    packaging: ["100 gms Box"],
    coverage: "Varies with joint width & tile dimensions",
    features: [
      "Enhances aesthetic look of tile joints",
      "Three finishes: gold, silver, copper",
      "For tiles & stones",
      "Internal & external areas",
      "Mixes with Ashlar Epoxy Grout",
    ],
    applications: ["Decorative joints", "Feature walls", "Luxury interiors"],
    substrates: ["Epoxy grout joints up to 12 mm"],
  },
  {
    slug: "grout-admixture-aga01",
    code: "AGA01",
    name: "Liquid Grout Admixture",
    category: "admixtures",
    category_name: "Admixtures & Additives",
    classification: "Latex Additive",
    tagline: "Liquid polymer that upgrades any cementitious grout.",
    description:
      "Ashlar AGA01 is a liquid polymer additive used with grout for improved performance — greater flexibility, adhesion, and water/chemical resistance. Suitable for any colour grout.",
    image: "/products/aga01.jpeg",
    color: "Milky white liquid",
    packaging: ["200 ml Bottle", "400 ml Bottle"],
    coverage: "Dose as per grout mix requirement",
    features: [
      "Improves flexibility & water resistance",
      "Improves adhesion strength",
      "Shock & chemical resistant",
      "Suitable for any colour grout",
      "Weather resistant",
    ],
    applications: ["Cementitious grout upgrade", "Wet areas", "Exterior joints"],
    substrates: ["Mix with Ashlar Tile Grout"],
  },
  {
    slug: "sbr-latex-abl01",
    code: "ABL01",
    name: "SBR Latex — Waterproofing & Bonding",
    category: "admixtures",
    category_name: "Admixtures & Additives",
    classification: "SBR Latex",
    tagline: "Styrene-butadiene rubber modifier for waterproofing, repair & bonding.",
    description:
      "Ashlar ABL01 is a latex-based additive for waterproofing, repair and bonding. It modifies concrete and cement mortars, strengthens the bond between new and old surfaces, improves flexibility and enhances water, chemical, abrasion and impact resistance.",
    image: "/products/abl01.jpeg",
    color: "Milky white liquid",
    packaging: ["1 L", "5 L", "20 L"],
    coverage: "As per bonding / waterproofing application",
    features: [
      "Modifier for concrete & cement mortars",
      "Bonds new & old concrete and plasters",
      "Improves flexibility & reduces cracking",
      "Enhances water & chemical resistance",
      "Boosts bonding strength of tile adhesives",
    ],
    applications: ["Waterproofing", "Repair mortars", "Bonding slurry", "Plaster modification"],
    substrates: ["Concrete", "Cement mortar", "Plaster"],
  },
  {
    slug: "aac-block-adhesive-aba01",
    code: "ABA01",
    name: "AAC Block Laying Adhesive",
    category: "block-adhesive",
    category_name: "Block Adhesive",
    classification: "Thin-bed Adhesive",
    tagline: "Factory-prepared thin-bed adhesive for AAC blocks & bricks.",
    description:
      "Ashlar ABA01 is a factory-prepared, polymer-modified adhesive for laying autoclaved aerated concrete (AAC) blocks, fly-ash bricks and similar cement-based blocks. A very thin bed improves coverage; no curing required.",
    image: "/products/aba01.jpeg",
    color: "Grey",
    packaging: ["40 KG Bag"],
    coverage: "Approx. 100 sq.ft. per 30 kg at 2 mm bed thickness",
    features: [
      "For AAC blocks, fly-ash & concrete blocks",
      "Long open time & fast application",
      "Very thin bed — improved coverage",
      "No curing required",
      "Single component — just add water",
    ],
    applications: ["AAC block masonry", "Fly-ash bricks", "Concrete blocks", "Cellular concrete"],
    substrates: ["Cement-based blocks & bricks"],
  },
  {
    slug: "ready-mix-plaster",
    code: "ARM01",
    name: "Ready Mix Plaster",
    category: "surface-solutions",
    category_name: "Plaster & Flooring",
    classification: "Ready-Mix",
    tagline: "Uniform, smooth plaster — just add water.",
    description:
      "Ashlar Ready Mix Plaster is a convenient factory-blended plaster for all types of masonry walls, interior and exterior. Requires only water, needs lesser curing than traditional plaster and delivers a uniform, smooth finish.",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=1600&auto=format&fit=crop",
    color: "Grey",
    packaging: ["40 KG Bag"],
    coverage: "Varies with plaster thickness",
    features: [
      "For all types of masonry walls",
      "Interior & exterior application",
      "Just add water — no sand or cement",
      "Needs lesser curing than traditional plaster",
      "Uniform & smooth finish",
    ],
    applications: ["Masonry walls", "Interior plaster", "Exterior plaster"],
    substrates: ["Brick", "Block", "Concrete"],
  },
  {
    slug: "floor-hardener",
    code: "AFH01",
    name: "Floor Hardener",
    category: "surface-solutions",
    category_name: "Plaster & Flooring",
    classification: "Surface Hardener",
    tagline: "Dust-proof, wear-resistant industrial flooring.",
    description:
      "Ashlar Floor Hardener enhances the durability and resistance of industrial and warehouse flooring, especially floors subjected to severe mechanical wear. It improves wear, impact, oil and grease resistance and makes floors dust-proof and easy to clean.",
    image: "/products/floor-hardener.jpeg",
    color: "Grey / Natural",
    packaging: ["40 KG Bag"],
    coverage: "As per specified floor loading",
    features: [
      "For industrial & warehouse flooring",
      "Good wear & high impact resistance",
      "Increased oil & grease resistance",
      "Makes floor dust-proof & easy to clean",
      "ISO 9001:2015 certified quality",
    ],
    applications: ["Warehouses", "Factories", "Parking decks", "Heavy-traffic floors"],
    substrates: ["Fresh concrete floors"],
  },
];

